import { SearchIcon } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import type { IntakeLookupResult, IntakeOption } from "@/api/ticket-intake";
import { Loader } from "@/components/motion/loader";
import { Button } from "@/components/ui/button";
import { InputGroup, InputGroupAddon, InputGroupButton, InputGroupInput } from "@/components/ui/input-group";
import { useIntakeMessages } from "./intake-messages";
import { IntakeRequestSequence } from "./intake-model";

export type IntakeLookup = (capability: string, search: string, signal: AbortSignal) => Promise<IntakeLookupResult>;

export function IntakeLookupControl({
  capability,
  scope,
  value,
  label,
  inputId,
  searchLabel,
  multiple,
  disabled,
  invalid,
  required,
  descriptionId,
  lookup,
  onChange,
  onRemove,
}: {
  capability: string;
  scope: string;
  value: unknown;
  label?: string;
  inputId?: string;
  searchLabel?: string;
  multiple?: boolean;
  disabled?: boolean;
  invalid?: boolean;
  required?: boolean;
  descriptionId?: string;
  lookup?: IntakeLookup;
  onChange: (option: IntakeOption | null) => void;
  onRemove?: (value: string) => void;
}) {
  const m = useIntakeMessages();
  const id = useId();
  const [search, setSearch] = useState("");
  const [response, setResponse] = useState<{ scope: string; data: IntakeLookupResult } | null>(null);
  const [failure, setFailure] = useState<{ scope: string; message: string }>();
  const [labels, setLabels] = useState<Record<string, string>>({});
  const [pending, setPending] = useState(false);
  const sequence = useRef(new IntakeRequestSequence());
  const controller = useRef<AbortController | null>(null);
  const activeScope = useRef(scope);
  const result = response?.scope === scope ? response.data : null;
  const error = failure?.scope === scope ? failure.message : undefined;
  useEffect(() => {
    activeScope.current = scope;
    sequence.current.invalidate();
    controller.current?.abort();
    setResponse(null);
    setFailure(undefined);
    setPending(false);
    return () => {
      sequence.current.invalidate();
      controller.current?.abort();
    };
  }, [scope]);

  const run = async () => {
    if (!lookup || disabled) return;
    controller.current?.abort();
    const abort = new AbortController();
    controller.current = abort;
    const current = sequence.current.start();
    setPending(true);
    setFailure(undefined);
    try {
      const response = await lookup(capability, search, abort.signal);
      if (sequence.current.isCurrent(current) && !abort.signal.aborted) setResponse({ scope, data: response });
    } catch (cause) {
      if (sequence.current.isCurrent(current) && !abort.signal.aborted) setFailure({ scope, message: cause instanceof Error ? cause.message : m.error });
    } finally {
      if (sequence.current.isCurrent(current)) setPending(false);
    }
  };

  return (
    <div className="flex min-w-0 flex-col gap-2">
      {multiple && Array.isArray(value) ? (
        value.map((item) => (
          <div className="flex items-center justify-between gap-2 rounded-lg bg-muted/40 px-3 py-2 text-sm" key={String(item)}>
            <span className="min-w-0 wrap-break-word">{labels[String(item)] || String(item)}</span>
            <Button
              aria-label={`${m.remove} ${labels[String(item)] || String(item)}`}
              disabled={disabled}
              className="shrink-0"
              size="sm"
              type="button"
              variant="ghost"
              onClick={() => onRemove?.(String(item))}
            >
              {m.remove}
            </Button>
          </div>
        ))
      ) : value ? (
        <div className="flex items-center justify-between gap-2 rounded-lg bg-muted/40 px-3 py-2 text-sm">
          <span className="min-w-0 wrap-break-word">{label || labels[String(value)] || String(value)}</span>
          <Button className="shrink-0" aria-label={m.clear} disabled={disabled} size="sm" type="button" variant="ghost" onClick={() => onChange(null)}>
            {m.clear}
          </Button>
        </div>
      ) : null}
      <InputGroup className="h-9">
        <InputGroupInput
          aria-label={searchLabel ?? m.searchHint}
          aria-invalid={invalid}
          aria-required={required}
          aria-describedby={descriptionId}
          disabled={disabled || pending || !lookup}
          id={inputId ?? id}
          placeholder={searchLabel ?? m.searchHint}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              void run();
            }
          }}
        />
        <InputGroupAddon align="inline-end">
          <InputGroupButton disabled={disabled || pending || !lookup} onClick={() => void run()}>
            {pending ? <Loader /> : <SearchIcon />}
          </InputGroupButton>
        </InputGroupAddon>
      </InputGroup>
      {error ? (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      ) : null}
      {result ? (
        <div className="flex max-h-48 flex-col gap-1 overflow-y-auto">
          {result.items.length === 0 ? (
            <p className="p-2 text-sm text-muted-foreground">{m.noResults}</p>
          ) : (
            result.items.map((item) => (
              <Button
                className="h-auto w-full justify-start whitespace-normal text-left"
                disabled={disabled}
                key={item.value}
                size="sm"
                type="button"
                variant="ghost"
                onClick={() => {
                  setLabels((current) => ({ ...current, [item.value]: item.label }));
                  onChange(item);
                  if (!multiple) setResponse(null);
                }}
              >
                {item.label}
              </Button>
            ))
          )}
          {result.hasMore ? <p className="p-2 text-xs text-muted-foreground">{m.moreResults}</p> : null}
        </div>
      ) : null}
    </div>
  );
}
